/**
 * Schlichte Verlaufslinie — ohne Diagramm-Bibliothek.
 *
 * `null` ist eine **Lücke**: Die Linie bricht dort ab, statt auf 0 zu fallen —
 * eine Woche ohne Wiegen hat kein Gewicht von 0 kg. Einzelne Punkte zwischen
 * Lücken stehen als Punkt da. Für Vorleseprogramme trägt die Grafik eine
 * Zusammenfassung in Worten (`summary`), denn eine Linie sagt dort nichts.
 */
export function Sparkline({
  values,
  summary,
  height = 48,
}: {
  values: readonly (number | null)[];
  summary: string;
  height?: number;
}): React.JSX.Element {
  const width = 300;
  const pad = 4;
  const present = values.filter((value): value is number => value !== null);
  const min = present.length ? Math.min(...present) : 0;
  const max = present.length ? Math.max(...present) : 1;
  const span = max - min || 1;
  const x = (index: number) =>
    values.length <= 1 ? width / 2 : pad + (index * (width - 2 * pad)) / (values.length - 1);
  const y = (value: number) => height - pad - ((value - min) / span) * (height - 2 * pad);

  // In zusammenhängende Stücke zerlegen — jede Lücke beginnt ein neues.
  const segments: { index: number; value: number }[][] = [];
  let current: { index: number; value: number }[] = [];
  values.forEach((value, index) => {
    if (value === null) {
      if (current.length) segments.push(current);
      current = [];
    } else current.push({ index, value });
  });
  if (current.length) segments.push(current);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={summary}
      className="h-12 w-full text-accent"
    >
      {segments.map((segment) =>
        segment.length === 1 ? (
          <circle
            key={segment[0]!.index}
            cx={x(segment[0]!.index)}
            cy={y(segment[0]!.value)}
            r={3}
            fill="currentColor"
          />
        ) : (
          <polyline
            key={segment[0]!.index}
            points={segment.map((point) => `${x(point.index)},${y(point.value)}`).join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ),
      )}
    </svg>
  );
}
